// Pulse / PWM oscillator built from a sawtooth + two WaveShapers.
// Technique adapted from pendragon-andyh/WebAudio-PulseOscillator.
//
// A sawtooth (-1..+1) is fed through a threshold shaper that snaps it to a
// square wave. Adding a DC offset (the "width") before the threshold shifts the
// zero-crossing, which changes the duty cycle — i.e. pulse-width modulation.

import type { VoiceSource } from "./VoiceSource";

// Threshold curve: input < 0 -> -1, input >= 0 -> +1.
function makePulseCurve(): Float32Array {
  const curve = new Float32Array(256);
  for (let i = 0; i < 128; i++) {
    curve[i] = -1;
    curve[i + 128] = 1;
  }
  return curve;
}

// Maps any input to a constant +1, so a gain node downstream emits its own
// gain value as a steady DC offset (this is how "width" becomes a DC signal).
const CONSTANT_ONE_CURVE = new Float32Array([1, 1]);

export function createPulseOscillator(ctx: BaseAudioContext): VoiceSource {
  const saw = new OscillatorNode(ctx, { type: "sawtooth" });

  const pulseShaper = new WaveShaperNode(ctx, { curve: makePulseCurve() });
  saw.connect(pulseShaper);

  // widthGain.gain is borrowed as the public "width" AudioParam.
  const widthGain = new GainNode(ctx, { gain: 0 });
  const constantOneShaper = new WaveShaperNode(ctx, { curve: CONSTANT_ONE_CURVE });
  saw.connect(constantOneShaper);
  constantOneShaper.connect(widthGain);
  widthGain.connect(pulseShaper);

  return {
    output: pulseShaper,
    frequency: saw.frequency,
    detune: saw.detune,
    width: widthGain.gain,
    start: (when) => saw.start(when),
    stop: (when) => saw.stop(when),
  };
}
